import { useId, useState } from 'react'
import DatePicker from './DatePicker'
import { formatDate, parseDate, type DateFormat } from './dates'

export type DateRange = { start: string; end: string }

export type DateRangePickerProps = {
  /** Names the range as a whole, such as "Travel dates". */
  legend: string
  /** A sentence under the legend about the range, such as how long it may be. */
  hint?: string
  startLabel?: string
  endLabel?: string
  /** The start date in the field's format, or empty. */
  start?: string
  /** The end date in the field's format, or empty. */
  end?: string
  /** Month first (MM/DD/YYYY, the default) or ISO 8601 (YYYY-MM-DD). */
  format?: DateFormat
  onChange?: (range: DateRange) => void
}

/**
 * Two date pickers for a start and an end, grouped in a fieldset. The end may
 * not come before the start: each field's hint names the limit the other date
 * sets, and an end before the start is reported in an error tied to the group.
 */
export default function DateRangePicker({
  legend,
  hint,
  startLabel = 'Start date',
  endLabel = 'End date',
  start = '',
  end = '',
  format = 'mdy',
  onChange,
}: DateRangePickerProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const limitsId = `${id}-limits`
  const errorId = `${id}-error`
  const [range, setRange] = useState<DateRange>({ start, end })
  // A new range from the parent, such as a reset or saved data, replaces what is typed.
  const [given, setGiven] = useState<DateRange>({ start, end })
  if (start !== given.start || end !== given.end) {
    setGiven({ start, end })
    setRange({ start, end })
  }

  const from = parseDate(range.start, format)
  const to = parseDate(range.end, format)
  const reversed = !!from && !!to && to < from

  const update = (next: Partial<DateRange>) => {
    const merged = { ...range, ...next }
    setRange(merged)
    onChange?.(merged)
  }

  const limits = [
    to ? `${startLabel}: on or before ${formatDate(to, format)}.` : '',
    from ? `${endLabel}: on or after ${formatDate(from, format)}.` : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <fieldset
      className="mds-date-range"
      aria-describedby={[hint ? hintId : '', limits ? limitsId : '', reversed ? errorId : ''].filter(Boolean).join(' ') || undefined}
    >
      <legend className="mds-label mds-date-range__legend">{legend}</legend>
      {hint && (
        <span className="mds-hint" id={hintId}>
          {hint}
        </span>
      )}
      <div className="mds-date-range__fields">
        <DatePicker label={startLabel} value={start} format={format} max={to ?? undefined} onChange={(value) => update({ start: value })} />
        <DatePicker label={endLabel} value={end} format={format} min={from ?? undefined} onChange={(value) => update({ end: value })} />
      </div>
      <span className="mds-date-range__limits" id={limitsId} aria-live="polite">
        {limits}
      </span>
      <span className="mds-error-text" id={errorId} role="status">
        {reversed && from ? `The end date must be on or after the start date, ${formatDate(from, format)}.` : ''}
      </span>
    </fieldset>
  )
}
